import { useEffect, useRef } from "react"

import { Button, Dropdown, Flex, Form, Text, TextArea } from "@/atoms"
import { PlusOutlined } from "@/icons"
import { SlackMessage } from "@/pages/GovernancePage/components/molecules/SlackMessage"
import {
  fillTagExamples,
  insertTag,
  SLACK_MESSAGE_MAX_LENGTH
} from "@/pages/GovernancePage/helpers/slackMessage"

import styles from "../ActionsSectionForm.module.scss"

import type { DynamicTag } from "@/api/useWorkflow.types"
import type { FormInstance } from "@/atoms"
import type { TextAreaRef } from "antd/es/input/TextArea"
import type { ActionProps } from ".."

interface SlackMessageTemplateFieldProps extends Pick<
  ActionProps,
  "path" | "t"
> {
  form: FormInstance
  tags: DynamicTag[]
  defaultTemplate: string
}

const PREFIX = "settings.automatedActions.form.fields.slackMessage"

export const SlackMessageTemplateField = (
  props: SlackMessageTemplateFieldProps
) => {
  const { form, path, tags, defaultTemplate, t } = props
  const name = ["actions", path, "messageTemplate"]
  const template: string | undefined = Form.useWatch(name, form)
  const textAreaRef = useRef<TextAreaRef>(null)

  // A new trigger has other tags, so its default replaces the message; a saved message is kept on load.
  const previousDefault = useRef("")
  useEffect(() => {
    if (!defaultTemplate) return
    const field = ["actions", path, "messageTemplate"]
    const triggerChanged =
      !!previousDefault.current && previousDefault.current !== defaultTemplate
    previousDefault.current = defaultTemplate
    if (triggerChanged || form.getFieldValue(field) === undefined)
      form.setFieldValue(field, defaultTemplate)
  }, [defaultTemplate, form, path])

  const setTemplate = (value: string) => {
    form.setFields([{ name, value, touched: true }])
    form.validateFields([name])
  }

  const onInsertTag = (key: string) => {
    const element = textAreaRef.current?.resizableTextArea?.textArea
    const current: string = form.getFieldValue(name) || ""
    const { text, cursor } = insertTag(
      current,
      key,
      element?.selectionStart,
      element?.selectionEnd
    )
    setTemplate(text)
    requestAnimationFrame(() => {
      element?.focus()
      element?.setSelectionRange(cursor, cursor)
    })
  }

  return (
    <>
      <Form.Item
        required={false}
        name={[path, "messageTemplate"]}
        className={styles.endpointField}
        extra={t(`${PREFIX}.formattingHint`)}
        label={
          <Flex align="center" justify="space-between">
            <span>{t(`${PREFIX}.message`)}</span>
            <Flex gap={4}>
              <Button
                size="small"
                type="link"
                disabled={!defaultTemplate || template === defaultTemplate}
                onClick={() => setTemplate(defaultTemplate)}
              >
                {t(`${PREFIX}.useDefault`)}
              </Button>
              <Dropdown
                trigger={["click"]}
                disabled={!tags.length}
                menu={{
                  className: styles.slackTagsMenu,
                  items: tags.map(({ key, displayName, description }) => ({
                    key,
                    label: displayName,
                    title: description
                  })),
                  onClick: ({ key }) => onInsertTag(key)
                }}
              >
                <Button size="small" type="link" icon={<PlusOutlined />}>
                  {t(`${PREFIX}.insertTag`)}
                </Button>
              </Dropdown>
            </Flex>
          </Flex>
        }
        rules={[
          {
            required: true,
            whitespace: true,
            message: t("error.requiredField")
          },
          {
            max: SLACK_MESSAGE_MAX_LENGTH,
            message: t(`${PREFIX}.tooLong`, { count: SLACK_MESSAGE_MAX_LENGTH })
          }
        ]}
      >
        <TextArea
          ref={textAreaRef}
          autoSize={{ minRows: 4, maxRows: 14 }}
          className={styles.slackTemplate}
        />
      </Form.Item>
      {!!template?.trim() && (
        <Flex vertical gap={4}>
          <Text type="secondary">
            {t(`${PREFIX}.preview`)} · {t(`${PREFIX}.previewNote`)}
          </Text>
          <SlackMessage
            text={fillTagExamples(template, tags)}
            className={styles.slackPreview}
          />
        </Flex>
      )}
    </>
  )
}
